import React, { useRef, useState, useEffect } from "react";
import BaseModal from "../../modals/base-modal";
import FormField from "../../forms/form-field";
import { instanceMob } from "../../../utils/utils";
import useLocationStore from "../config/use-location-store";
import InlineModal from "../../info-modal/infoModal";
import { getBackendError } from "../../../utils/error-helpers";
import FormSelect from "../../forms/form-select";
import Tooltip from "../../tooltip/tooltip";
import DisclaimerModal from "../../disclaimer-modal/disclaimer-modal";

const InventoryMovementModal = ({ show, onClose }) => {
    const formRef = useRef(null);
    const [isLoading, setIsLoading] = useState(false);
    const [infoModal, setInfoModal] = useState(null);
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [brands, setBrands] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedBrand, setSelectedBrand] = useState(null);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [lockedField, setLockedField] = useState(null);
    const [selectedProductData, setSelectedProductData] = useState({
        product_id: "",
        product_store_id: "",
    });
    const selectedLocation = useLocationStore((state) => state.selectedLocation);

    const [showDisclaimer, setShowDisclaimer] = useState(false);
    useEffect(() => {
        if (show) {
            setShowDisclaimer(true);
        } else {
            setShowDisclaimer(false);
        }
    }, [show]);

    const handleProductsChange = (productId) => {
        const product = products.find((p) => String(p.product_id) === String(productId));
        setSelectedProduct(productId);

        if (product) {
            setSelectedProductData({
                product_id: product.product_id,
                product_store_id: product.product_store_id || "",
            });

            setLockedField("product");

            setSelectedBrand(String(product.brand_id || ""));
            setSelectedCategory(
                product.category_ids ? product.category_ids.map((c) => String(c)) : []
            );
        } else {
            setLockedField(null);
            setSelectedProductData({ product_id: "", product_store_id: "" });
            setSelectedBrand(null);
            setSelectedCategory([]);
        }
    };


    const handleCategoryChange = (e) => {
        const value = Array.isArray(e.target.value) ? e.target.value : [e.target.value];
        setSelectedCategory(value);
        if (!lockedField) setLockedField("category");
    };

    const handleBrandChange = (e) => {
        const value = e.target.value;
        setSelectedBrand(value);
        if (!lockedField) setLockedField("brand");
    };

    const formFields = [
        {
            Component: FormField,
            label: "Fecha",
            name: "saleDate",
            type: "date",
            value: new Date().toISOString().split("T")[0],
            readOnly: true,
            placeholder: "Fecha",
            width: "65%",
            isLoading,
        },
        {
            Component: FormSelect,
            label: "Productos",
            name: "products",
            type: "select",
            placeholder: "Seleccione los productos",
            required: true,
            isLoading,
            options: products.map((p) => ({ value: p.product_id, label: p.name })),
            searchable: true,
            onChange: ({target}) => handleProductsChange(target.value),
            tooltip: "Nombre del artículo seleccionado en el inventario. Puedes buscarlo escribiendo parte del nombre o seleccionándolo de la lista desplegable.",
            value: selectedProduct || "",
            disabled: lockedField === "brand" || lockedField === "category",
        },
        {
            Component: FormField,
            label: "ID del producto",
            name: "product_store_id",
            type: "text",
            placeholder: "automático",
            readOnly: true,
            isLoading,
            value: selectedProductData.product_store_id,
        },
        {
            Component: FormSelect,
            label: "Categoría",
            name: "category_ids",
            options: categories,
            value: selectedCategory || [],
            placeholder: "Selecciona la categoría",
            searchable: true,
            isLoading,
            onChange: handleCategoryChange,
            disabled: lockedField === "product" || lockedField === "brand",
            multiple: true,
        },
        {
            Component: FormSelect,
            label: "Marca",
            name: "brand_id",
            options: brands,
            value: selectedBrand || "",
            placeholder: "Selecciona la marca",
            searchable: true,
            isLoading,
            onChange: handleBrandChange,
            disabled: lockedField === "product" || lockedField === "category",
        },
    ];

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        const params = {
            location_id: selectedLocation?.id || "",
        };

        if (lockedField === "product") {
            params.product_id = selectedProductData.product_id;
        } else if (lockedField === "category") {
            params.categories = (selectedCategory || []).join(",");
        } else if (lockedField === "brand") {
            params.brand = selectedBrand;
        } else if (!lockedField && selectedProductData.product_id) {
            params.product_id = selectedProductData.product_id;
        }

        try {
            const response = await instanceMob.get("/admin/accounting/stock/excel", {
                params,
                responseType: "blob",
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;

            const fileSuffix =
                params.product_id ||
                params.brand ||
                params.categories?.replaceAll(",", "_") ||
                "all";

            link.setAttribute("download", `inventory_movement_${fileSuffix}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            setInfoModal({
                iconType: "success",
                title: "Informe descargado",
                description: "El informe de inventario fue generado y descargado con éxito.",
                primaryAction: {
                    label: "Aceptar",
                    onClick: () => {
                        setInfoModal(null);
                        onClose();
                    },
                },
            });
        } catch (err) {
            const errorMessage = getBackendError(err);
            setInfoModal({
                iconType: "error",
                title: "Error",
                description: errorMessage,
                secondaryAction: {
                    label: "Cerrar",
                    onClick: () => setInfoModal(null),
                },
            });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!selectedLocation?.id) return;
        setIsLoading(true);

        const productsPromise = instanceMob
            .get("admin/accounting/products/list", {
                params: { location_id: selectedLocation.id },
            })
            .then((res) => res.data || [])
            .catch(() => []);

        const categoriesPromise = instanceMob
            .get("admin/accounting/category/")
            .then((res) => res.data?.map((c) => ({ value: String(c.category_id), label: c.name })) || [])
            .catch(() => []);

        const brandsPromise = instanceMob
            .get("admin/accounting/brand/")
            .then((res) => res.data?.map((b) => ({ value: String(b.brand_id), label: b.name })) || [])
            .catch(() => []);

        Promise.all([productsPromise, categoriesPromise, brandsPromise])
            .then(([productsData, categoriesData, brandsData]) => {
                setProducts(productsData);
                setCategories(categoriesData);
                setBrands(brandsData);
            })
            .finally(() => setIsLoading(false));
    }, [selectedLocation?.id]);

    return (
        <>
            {showDisclaimer && (
                <DisclaimerModal
                    storageKey="inventory_disclaimer_seen"
                    title="Información importante"
                    description="Puedes seleccionar un producto y los campos ID del producto, categoría y marca se llenarán automáticamente. Si lo prefieres, también puedes filtrar por categoría o marca de forma individual para descargar un informe personalizado."
                    variant="warning"
                />
            )}

            <BaseModal
                show={show}
                onClose={() => onClose()}
                title="Movimiento de inventario"
                onSave={() => formRef.current?.requestSubmit()}
                showCancelButton={true}
                saveText="Descargar informe"
            >
                <form className="d-flex flex-column gap-2" ref={formRef} onSubmit={handleSubmit}>
                    {formFields.map((field, index) => {
                        const FieldComponent = field.Component;
                        const inputElement = <FieldComponent key={index} id={field.name} {...field} />;

                        return field.tooltip && field.type !== "hidden" ? (
                            <Tooltip
                                key={index}
                                title={field.label}
                                description={field.tooltip}
                                position="right"
                                variant="rich"
                            >
                                {inputElement}
                            </Tooltip>
                        ) : inputElement;
                    })}

                    <input
                        type="hidden"
                        name="location_id"
                        value={selectedLocation?.id || ""}
                    />
                    <input
                        type="hidden"
                        name="product_id"
                        value={selectedProductData.product_id || ""}
                    />
                </form>
            </BaseModal>

            {infoModal && (
                <InlineModal
                    icon={infoModal.icon}
                    iconType={infoModal.iconType}
                    title={infoModal.title}
                    description={infoModal.description}
                    primaryAction={infoModal.primaryAction}
                    secondaryAction={infoModal.secondaryAction}
                />
            )}
        </>
    );
};

export default InventoryMovementModal;
